import { useEffect, useState } from 'react'
import Panel from '../ui/Panel'
import TestBeacons from '../features/leak-test/TestBeacons'
import PollTracker from '../features/leak-test/PollTracker'
import VerdictPanel from '../features/leak-test/VerdictPanel'
import ResolverTable from '../features/leak-test/ResolverTable'
import ShareBar from '../features/leak-test/ShareBar'
import SavedTests from '../features/leak-test/SavedTests'
import AdUnit from '../features/ads/AdUnit'
import ShareBars from '../charts/ShareBars'
import OrgConcentration from '../charts/OrgConcentration'
import WorldPlot from '../charts/WorldPlot'
import DiscoveryTimeline from '../charts/DiscoveryTimeline'
import DistanceStat from '../charts/DistanceStat'
import { useSelf } from '../hooks/useSelf'
import { makeIdentifier, useLeakTest } from '../hooks/useLeakTest'
import { leakTest } from '../content/site'
import styles from './LeakTest.module.css'

export default function LeakTest() {
    const self = useSelf()
    const [identifier, setIdentifier] = useState<string>()

    // a token can only be minted once the address is known: it encodes the ip
    useEffect(() => {
        if (self.data?.ip && !identifier) setIdentifier(makeIdentifier(self.data.ip))
    }, [self.data?.ip, identifier])

    const test = useLeakTest(identifier)

    return (
        <>
            {identifier && test.running && <TestBeacons identifier={identifier} />}

            <div className={styles.head}>
                <h1>DNS leak test for <span className={styles.subject}>{self.data?.ip ?? '…'}</span></h1>
                {!test.running && (
                    <ShareBar
                        rows={test.rows}
                        self={self.data}
                        hostName={self.data?.asnOrg ?? self.data?.isp ?? ''}
                        onRetest={() => setIdentifier(makeIdentifier(self.data?.ip ?? '0.0.0.0'))}
                    />
                )}
            </div>

            <Panel title={test.running ? leakTest.running : 'Complete'}>
                <PollTracker
                    poll={test.poll}
                    running={test.running}
                    elapsedMs={test.elapsedMs}
                    resolverCount={test.rows.length}
                    firstSeen={test.firstSeen}
                    onStop={test.stop}
                />
            </Panel>

            {!test.running && test.rows.length > 0 && (
                <div className={styles.section}>
                    <VerdictPanel rows={test.rows} self={self.data} />
                </div>
            )}

            {test.rows.length > 0 && (
                <>
                    <div className={styles.charts}>
                        <ShareBars rows={test.rows} />
                        <OrgConcentration rows={test.rows} self={self.data} />
                    </div>

                    <div className={styles.charts}>
                        <DiscoveryTimeline firstSeen={test.firstSeen} />
                        <DistanceStat rows={test.rows} self={self.data} />
                    </div>
                </>
            )}

            <div className={styles.section}>
                <Panel title="Resolvers">
                    <ResolverTable rows={test.rows} self={self.data} />
                </Panel>
            </div>

            {test.rows.length > 0 && (
                <div className={styles.section}>
                    <WorldPlot rows={test.rows} self={self.data} />
                </div>
            )}

            {/* Reserved slot for the responsive ad unit. It has no min-height
                and collapses when empty, so a blocked or unfilled unit -- or a
                build with no AdSense id -- leaves no hole in the page. */}
            <div className={styles.adSlot}>
                <AdUnit slot="2766573840" />
            </div>

            <SavedTests />

            <div className={styles.section}>
                <Panel title={leakTest.resolvers.heading}>
                    {leakTest.resolvers.body.map((p) => <p key={p} className={styles.para}>{p}</p>)}
                </Panel>
            </div>

            <div className={styles.section}>
                <Panel title={leakTest.understanding.heading}>
                    {leakTest.understanding.body.map((p) => <p key={p} className={styles.para}>{p}</p>)}
                </Panel>
            </div>
        </>
    )
}
